import { useState, useMemo } from 'react';
import { fmtPrice, fmtPct } from '../utils/format';
import { rsiState } from '../utils/indicators';
import { useCoinRSI } from '../utils/rsi';
import MTFAnalysis from './MTFAnalysis';
import AltseasonIndex from './AltseasonIndex';
import TrendingCoins from './TrendingCoins';
import InfoTooltip from './InfoTooltip';
import SourceDot, { SourceAttr, mergeMeta } from './SourceDot';
import DataSourceDown from './DataSourceDown';
import { openAlertDraft } from '../utils/alerts';

const RSI_TF = 'D';

const STATE_BG = { oversold: '#001f2a', overbought: '#2a0d0d', neutral: '#1a1a1a', na: 'transparent' };

function detectTrend(change1h, change24h, change7d) {
  const c1h  = change1h  ?? 0;
  const c24h = change24h ?? 0;
  const c7d  = change7d  ?? 0;
  const score = Math.sign(c1h) + Math.sign(c24h) * 2 + Math.sign(c7d) * 3;
  if (score >= 4)  return { text: '▲ Erős Emelkedő Trend', color: '#00c853' };
  if (score >= 2)  return { text: '↗ Emelkedő Trend',      color: '#69f0ae' };
  if (score <= -4) return { text: '▼ Erős Csökkenő Trend', color: '#ff4d4d' };
  if (score <= -2) return { text: '↘ Csökkenő Trend',      color: '#ff8a80' };
  return               { text: '→ Oldalazó',              color: '#888'    };
}

function srLevels(ath, atl, current) {
  if (!ath || !atl || !current) return null;
  const range = ath - atl;
  return {
    r2: ath,
    r1: atl + range * 0.786,
    pivot: atl + range * 0.618,
    s1: atl + range * 0.382,
    s2: atl + range * 0.236,
    s3: atl,
    positionPct: Math.min(100, Math.max(0, ((current - atl) / range) * 100)),
  };
}

function RsiBar({ rsi }) {
  return (
    <div className="sig-rsi-wrap">
      <div className="sig-rsi-track">
        <div className="sig-rsi-zone ob" />
        <div className="sig-rsi-zone neutral" />
        <div className="sig-rsi-zone os" />
        <div className="sig-rsi-thumb" style={{ left: rsi + '%' }} />
      </div>
      <div className="sig-rsi-labels">
        <span style={{ color: '#00e5ff' }}>Túlad.</span>
        <span style={{ color: '#888' }}>30 — 70</span>
        <span style={{ color: '#ff4d4d' }}>Túlv.</span>
      </div>
    </div>
  );
}

function SRBar({ sr }) {
  return (
    <div className="sr-bar-wrap">
      <div className="sr-bar-track">
        <div className="sr-bar-fill" style={{ width: sr.positionPct + '%' }} />
        {[sr.s2, sr.s1, sr.pivot, sr.r1].map((lvl, i) => {
          const pct = Math.min(100, Math.max(0, ((lvl - sr.s3) / (sr.r2 - sr.s3)) * 100));
          return <div key={i} className="sr-level-tick" style={{ left: pct + '%' }} />;
        })}
      </div>
    </div>
  );
}

const SIGNAL_FILTERS = [
  { key: 'all',        label: 'Mind'     },
  { key: 'oversold',   label: 'Túladott' },
  { key: 'overbought', label: 'Túlvett'  },
  { key: 'neutral',    label: 'Semleges' },
];

const SORT_OPTIONS = [
  { value: 'rank',   label: 'Sorrend szerint' },
  { value: 'rsi',    label: 'RSI szerint'      },
  { value: 'change', label: 'Változás szerint' },
  { value: 'name',   label: 'Név szerint'      },
];

export default function SignalsTab({ coins, selectedCoinId, globalData, trendingData, onSelectCoin, marketsMeta, globalMeta, trendingMeta, previewLimit }) {
  const [filterText,   setFilterText]   = useState('');
  const [signalFilter, setSignalFilter] = useState('all');
  const [sortBy,       setSortBy]       = useState('rank');

  /* FREE preview (ProPreview): only the first rows are readable, so RSI is
     fetched for those coins only (klines are 1 request per coin). */
  const rsiCoins = useMemo(() => (previewLimit ? (coins ?? []).slice(0, previewLimit) : coins), [coins, previewLimit]);
  const { rows: rsiRows, symbols, pending, meta: rsiMeta, tickersFailed, failed, retry } = useCoinRSI(rsiCoins, RSI_TF);
  const rsiIds = useMemo(() => new Set((rsiCoins ?? []).map(c => c.id)), [rsiCoins]);

  const enriched = useMemo(() => (coins ?? []).map(coin => {
    const r     = rsiRows[coin.id];
    const rsi   = r?.rsi ?? null;
    const lbl   = rsiState(rsi);
    const inScope = rsiIds.has(coin.id);                        // FREE preview: only the first rows are fetched
    const noPerp = symbols && coin.id in symbols ? !symbols[coin.id] : false;
    const trend = detectTrend(
      coin.price_change_percentage_1h_in_currency,
      coin.price_change_percentage_24h,
      coin.price_change_percentage_7d_in_currency,
    );
    const sr = srLevels(coin.ath, coin.atl, coin.current_price);
    return { coin, rsi, lbl, trend, sr, noPerp, short: r?.reason === 'short',
             bybit: symbols?.[coin.id] || null,
             // "…" only while a request is really running; otherwise "—"
             loading: inScope && !noPerp && !r && !tickersFailed };
  }), [coins, rsiRows, symbols, tickersFailed, rsiIds]);

  // Text-filtered list (used for summary counts)
  const textFiltered = useMemo(() => {
    const q = filterText.trim().toLowerCase();
    if (!q) return enriched;
    return enriched.filter(({ coin }) =>
      coin.symbol.toLowerCase().includes(q) || coin.name.toLowerCase().includes(q)
    );
  }, [enriched, filterText]);

  // Summary counts always from text-filtered list
  const counts = useMemo(() => ({
    oversold:   textFiltered.filter(r => r.lbl.key === 'oversold').length,
    overbought: textFiltered.filter(r => r.lbl.key === 'overbought').length,
    neutral:    textFiltered.filter(r => r.lbl.key === 'neutral').length,
    noPerp:     textFiltered.filter(r => r.noPerp).length,
    short:      textFiltered.filter(r => r.short).length,
  }), [textFiltered]);

  // Apply signal filter + sort
  const visible = useMemo(() => {
    let list = signalFilter === 'all'
      ? textFiltered
      : textFiltered.filter(r => r.lbl.key === signalFilter);

    if (sortBy === 'rsi')    list = [...list].sort((a, b) => (b.rsi ?? -1) - (a.rsi ?? -1));
    if (sortBy === 'change') list = [...list].sort((a, b) =>
      (b.coin.price_change_percentage_24h ?? 0) - (a.coin.price_change_percentage_24h ?? 0));
    if (sortBy === 'name')   list = [...list].sort((a, b) =>
      a.coin.name.localeCompare(b.coin.name));
    return list;
  }, [textFiltered, signalFilter, sortBy]);

  return (
    <div className="signals-tab">

      <AltseasonIndex coins={coins} globalData={globalData} meta={mergeMeta(marketsMeta, globalMeta)} />

      <TrendingCoins trending={trendingData} onSelect={onSelectCoin} watchlistCoins={coins} meta={trendingMeta} />

      {/* Filter toolbar */}
      <div className="sig-filter-bar">
        <input
          className="sig-search-input"
          type="text"
          placeholder="Keresés coin névben vagy szimbólumban…"
          value={filterText}
          onChange={e => setFilterText(e.target.value)}
        />
        <div className="sig-filter-btns">
          {SIGNAL_FILTERS.map(f => (
            <button
              key={f.key}
              className={`sig-filter-btn ${signalFilter === f.key ? 'active' : ''} ${f.key !== 'all' ? `sig-filter-btn--${f.key}` : ''}`}
              onClick={() => setSignalFilter(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="sig-sort-wrap">
          <span className="sig-sort-label">Rendezés:</span>
          <select
            className="sig-sort-select"
            value={sortBy}
            onChange={e => setSortBy(e.target.value)}
          >
            {SORT_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Summary bar */}
      <div className="sig-summary-bar">
        <SourceDot source="CoinGecko (árak) · Bybit perpetual (RSI, lezárt napi gyertyák)" meta={mergeMeta(marketsMeta, rsiMeta)} />
        {/* FREE-előnézetben az RSI csak az első sorokra jön le: az összesítő is csak azokból számol */}
        {previewLimit ? <span className="sig-summary-total">Előnézet, az első {previewLimit} coin alapján:</span> : null}
        <span className="sig-summary-pill" style={{ color: '#00e5ff' }}>{counts.oversold} túladott</span>
        <span className="sig-summary-sep">|</span>
        <span className="sig-summary-pill" style={{ color: '#ff4d4d' }}>{counts.overbought} túlvett</span>
        <span className="sig-summary-sep">|</span>
        <span className="sig-summary-pill sig-summary-pill--neutral">{counts.neutral} semleges</span>
        {counts.noPerp > 0 && (
          <span className="sig-summary-total">· {counts.noPerp} coinnak nincs Bybit perpetualja</span>
        )}
        {counts.short > 0 && (
          <span className="sig-summary-total">· {counts.short} coinnál kevés az előzmény (&lt; 250 nap)</span>
        )}
        {pending > 0 && (
          <span className="sig-summary-total">· {pending} még töltődik</span>
        )}
        {filterText && (
          <span className="sig-summary-total">— {visible.length} találat</span>
        )}
      </div>
      {(tickersFailed || failed > 0) && <DataSourceDown onRetry={retry} />}

      {/* Table header */}
      <div className="sig-header-row">
        <span className="sig-col-coin">Coin</span>
        <span className="sig-col-price">Ár</span>
        <span className="sig-col-changes">1h / 24h / 7d</span>
        <span className="sig-col-rsi">RSI 14 · 1D <InfoTooltip text="Valódi RSI (14, Wilder-simítás) a Bybit perpetual napi gyertyáiból, csak lezárt gyertyákból. 70 felett túlvett, 30 alatt túladott állapotot jelez. A TradingView-n az előző (lezárt) napi gyertya RSI-jével egyezik; a charton látott élő érték a még nyitott gyertya miatt eltérhet." /></span>
        <span className="sig-col-trend">Árirány <InfoTooltip text="Az 1 órás, 24 órás és 7 napos árváltozás iránya, súlyozva (a 7 nap számít a legtöbbet). Nem indikátor, csak az elmozdulások összefoglalója." /></span>
        <span className="sig-col-signal">Állapot <InfoTooltip text="Leíró állapot az RSI alapján: túladott (RSI < 30), túlvett (RSI > 70) vagy semleges. Nem vételi vagy eladási utasítás." /></span>
        <span className="sig-col-sr">Támasz / Ellenállás <InfoTooltip text="A támasz az az árszint, ahol a coin általában megáll esés közben — sokan vásárolnak itt. Az ellenállás az, ahol emelkedés közben akad el — sokan eladnak itt. A P% azt mutatja, hogy a jelenlegi ár hol helyezkedik el a coin valaha volt legalacsonyabb (ATL) és legmagasabb (ATH) ára között." /></span>
      </div>

      {visible.length === 0 && (
        <div className="sig-empty">Nincs találat a keresési feltételekre.</div>
      )}

      {visible.map(({ coin, rsi, lbl, trend, sr, noPerp, short, loading, bybit }) => (
        <div key={coin.id} className="sig-row">
          <div className="sig-col-coin">
            <img src={coin.image} alt={coin.symbol} className="sig-coin-icon" />
            <div>
              <div className="sig-coin-sym">{coin.symbol.toUpperCase()}</div>
              <div className="sig-coin-rank">#{coin.market_cap_rank}</div>
            </div>
            {bybit && (
              <button type="button" className="alert-row-btn" title="RSI-riasztás"
                aria-label={`RSI-riasztás: ${coin.symbol.toUpperCase()}`}
                onClick={() => openAlertDraft({ kind: 'rsi', symbol: bybit, rsi, interval: 'D', source: 'signals' })}>🔔</button>
            )}
          </div>

          <div className="sig-col-price">{fmtPrice(coin.current_price)}</div>

          <div className="sig-col-changes">
            <span className={coin.price_change_percentage_1h_in_currency >= 0 ? 'positive' : 'negative'}>
              {fmtPct(coin.price_change_percentage_1h_in_currency)}
            </span>
            <span className={coin.price_change_percentage_24h >= 0 ? 'positive' : 'negative'}>
              {fmtPct(coin.price_change_percentage_24h)}
            </span>
            <span className={coin.price_change_percentage_7d_in_currency >= 0 ? 'positive' : 'negative'}>
              {fmtPct(coin.price_change_percentage_7d_in_currency)}
            </span>
          </div>

          <div className="sig-col-rsi">
            {rsi != null ? (
              <>
                <div className="rsi-pill" style={{ color: lbl.color, background: STATE_BG[lbl.key] }}>
                  {rsi.toFixed(0)} <span style={{ opacity: 0.6, fontWeight: 400 }}>· 1D</span>
                </div>
                <RsiBar rsi={rsi} />
              </>
            ) : (
              <span className="ind-na" title={noPerp ? 'Ehhez a coinhoz nincs Bybit USDT perpetual' : undefined}>
                {noPerp ? '— nincs perp' : short ? '— kevés adat' : loading ? '…' : '—'}
              </span>
            )}
          </div>

          <div className="sig-col-trend" style={{ color: trend.color }}>{trend.text}</div>

          <div className="sig-col-signal">
            {rsi != null ? (
              <span className="sig-signal-badge" style={{ color: lbl.color, borderColor: lbl.color }}>
                {lbl.text}
              </span>
            ) : <span className="ind-na">—</span>}
          </div>

          <div className="sig-col-sr">
            {sr ? (
              <>
                <div className="sr-line sr-support">P {fmtPrice(sr.pivot)}</div>
                <div className="sr-line sr-resist">E {fmtPrice(sr.r1)}</div>
                <SRBar sr={sr} current={coin.current_price} />
              </>
            ) : <span className="ind-na">—</span>}
          </div>
        </div>
      ))}

      <SourceAttr cg />

      {!previewLimit && <MTFAnalysis coins={coins} selectedCoinId={selectedCoinId} />}
    </div>
  );
}
